import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Topbar from '../components/Topbar';
import api from '../api';

function initiales(prenom, nom) {
  return `${(prenom||'?')[0]}${(nom||'?')[0]}`.toUpperCase();
}

function ReponseEditable({ reponse, question, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');

  useEffect(() => {
    let v = reponse?.valeur || '';
    try { v = JSON.parse(v); if (Array.isArray(v)) v = v.join(', '); } catch {}
    setVal(v);
  }, [reponse]);

  const handleSave = async () => {
    await onSave(question.question_id || question.id, val);
    setEditing(false);
  };

  if (question.question_type === 'separateur') {
    return <h6 className="fw-semibold mt-4 mb-2 text-primary border-bottom pb-1">{question.question_libelle}</h6>;
  }

  const displayVal = val || '—';
  const wasModified = !!reponse?.modifie_par_enseignant;

  return (
    <div className={`card mb-2 ${wasModified ? 'reponse-modifiee' : ''}`}>
      <div className="card-body py-2 px-3">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div style={{ flex: 1 }}>
            <div className="text-muted mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {question.question_libelle}
              {wasModified && (
                <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.65rem' }}>
                  <i className="bi bi-pencil me-1"></i>Modifié
                </span>
              )}
            </div>
            {editing ? (
              <div className="d-flex gap-2 mt-1">
                {question.question_type === 'texte_long' ? (
                  <textarea className="form-control form-control-sm" rows={3} value={val} onChange={e => setVal(e.target.value)} />
                ) : (
                  <input className="form-control form-control-sm" value={val} onChange={e => setVal(e.target.value)} />
                )}
                <button className="btn btn-sm btn-primary" onClick={handleSave}><i className="bi bi-check2"></i></button>
                <button className="btn btn-sm btn-light" onClick={() => setEditing(false)}><i className="bi bi-x"></i></button>
              </div>
            ) : (
              <div className="fw-medium" style={{ fontSize: '0.9rem', color: val ? '#24292d' : '#aaa' }}>{displayVal}</div>
            )}
          </div>
          {!editing && (
            <button className="btn btn-sm btn-light mt-1" onClick={() => setEditing(true)}>
              <i className="bi bi-pencil"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const NOTE_CATEGORIES = ['Observation', 'Comportement', 'Résultats', 'Objectifs', 'Informations années antérieures', 'Autre'];

const SIGNALEMENT_TYPES = [
  { key: 'comportement', label: 'Comportement', icon: 'bi-exclamation-triangle', color: '#fd7e14' },
  { key: 'famille',      label: 'Famille',       icon: 'bi-house-door',           color: '#6f42c1' },
  { key: 'sante',        label: 'Santé',          icon: 'bi-heart',                color: '#0cbc87' },
  { key: 'scolaire',     label: 'Scolaire',       icon: 'bi-book',                 color: '#066ac9' },
  { key: 'autre',        label: 'Autre',          icon: 'bi-flag',                 color: '#6c757d' },
];

export default function FicheEleve() {
  const { id } = useParams();
  const [eleve, setEleve] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState({ categorie: 'Observation', contenu: '' });
  const [savingNote, setSavingNote] = useState(false);
  const [editNote, setEditNote] = useState(null);

  const load = () => api.get(`/eleves/${id}`).then(r => { setEleve(r.data); setLoading(false); }).catch(() => setLoading(false));

  useEffect(() => { load(); }, [id]);

  const handleSaveReponse = async (questionId, valeur) => {
    await api.put(`/eleves/${id}/reponse/${questionId}`, { valeur });
    load();
  };

  const handleAddNote = async e => {
    e.preventDefault();
    if (!newNote.contenu.trim()) return;
    setSavingNote(true);
    await api.post(`/eleves/${id}/notes`, newNote);
    setNewNote({ categorie: 'Observation', contenu: '' });
    setSavingNote(false);
    load();
  };

  const handleToggleSignalement = async (type) => {
    await api.post(`/eleves/${id}/signalements/${type}`);
    load();
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    await api.delete(`/eleves/${id}/notes/${noteId}`);
    load();
  };

  const handleSaveEditNote = async () => {
    await api.put(`/eleves/${id}/notes/${editNote.id}`, { categorie: editNote.categorie, contenu: editNote.contenu });
    setEditNote(null);
    load();
  };

  if (loading) return (
    <div className="d-flex align-items-center justify-content-center" style={{ height: '80vh' }}>
      <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
    </div>
  );
  if (!eleve) return <div className="page-content"><div className="alert alert-danger">Élève introuvable</div></div>;

  const retourUrl = eleve.classe_id ? `/classe/${eleve.classe_id}` : '/classes';

  return (
    <>
      <Topbar
        title="Fiche élève"
        actions={
          <Link to={retourUrl} className="btn btn-light">
            <i className="bi bi-arrow-left me-2"></i>Retour
          </Link>
        }
      />
      <div className="page-content">
        {/* Header élève */}
        <div className="eleve-header mb-4 d-flex align-items-center gap-4 flex-wrap">
          <div className="eleve-avatar">{initiales(eleve.prenom, eleve.nom)}</div>
          <div>
            <h4 className="mb-1 fw-bold">{eleve.prenom} {eleve.nom}</h4>
            <div className="d-flex gap-3 opacity-90 flex-wrap" style={{ fontSize: '0.875rem' }}>
              {eleve.classe_nom && (
                <span><i className="bi bi-people me-1"></i>{eleve.classe_nom}</span>
              )}
              {eleve.formulaire_titre && (
                <span><i className="bi bi-clipboard me-1"></i>{eleve.formulaire_titre}</span>
              )}
              <span><i className="bi bi-clock me-1"></i>
                {new Date(eleve.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* Réponses */}
          <div className="col-lg-7">
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between">
                <span className="fw-semibold">Réponses au formulaire</span>
                <span className="badge bg-light text-muted">{eleve.reponses?.length || 0}</span>
              </div>
              <div className="card-body">
                {(!eleve.reponses || eleve.reponses.length === 0) ? (
                  <p className="text-muted text-center py-3">Aucune réponse enregistrée.</p>
                ) : (
                  eleve.reponses.map((r, i) => (
                    <ReponseEditable
                      key={i}
                      reponse={r}
                      question={r}
                      onSave={handleSaveReponse}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Notes enseignant */}
          <div className="col-lg-5">
            <div className="card mb-3">
              <div className="card-header"><span className="fw-semibold">Signalements</span></div>
              <div className="card-body pb-2">
                <div className="d-flex gap-2 flex-wrap">
                  {SIGNALEMENT_TYPES.map(s => {
                    const active = (eleve.signalements || []).includes(s.key);
                    return (
                      <button
                        key={s.key}
                        title={s.label}
                        className="btn btn-sm d-flex align-items-center gap-1"
                        style={active ? {
                          background: s.color,
                          color: '#fff',
                          border: `1.5px solid ${s.color}`,
                          fontWeight: 500,
                        } : {
                          background: '#f0f2f5',
                          color: '#adb5bd',
                          border: '1.5px solid #dee2e6',
                        }}
                        onClick={() => handleToggleSignalement(s.key)}
                      >
                        <i className={`bi ${s.icon}`} style={{ fontSize: '0.9rem' }}></i>
                        <span style={{ fontSize: '0.8rem' }}>{s.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header"><span className="fw-semibold">Ajouter une note</span></div>
              <div className="card-body">
                <form onSubmit={handleAddNote}>
                  <div className="mb-2">
                    <select className="form-select form-select-sm" value={newNote.categorie}
                      onChange={e => setNewNote({ ...newNote, categorie: e.target.value })}>
                      {NOTE_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <textarea className="form-control form-control-sm mb-2" rows={3}
                    placeholder="Observation, remarque, objectif..."
                    value={newNote.contenu}
                    onChange={e => setNewNote({ ...newNote, contenu: e.target.value })}
                  />
                  <button type="submit" className="btn btn-sm btn-primary w-100" disabled={savingNote || !newNote.contenu.trim()}>
                    <i className="bi bi-plus me-1"></i>Ajouter
                  </button>
                </form>
              </div>
            </div>

            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between">
                <span className="fw-semibold">Notes enseignant</span>
                <span className="badge bg-light text-muted">{eleve.notes?.length || 0}</span>
              </div>
              <div className="card-body p-2">
                {(!eleve.notes || eleve.notes.length === 0) ? (
                  <p className="text-muted text-center py-3" style={{ fontSize: '0.875rem' }}>Aucune note.</p>
                ) : (
                  eleve.notes.map(note => (
                    <div key={note.id} className="card mb-2 border" style={{ borderRadius: 8 }}>
                      <div className="card-body py-2 px-3">
                        {editNote?.id === note.id ? (
                          <>
                            <select className="form-select form-select-sm mb-1" value={editNote.categorie}
                              onChange={e => setEditNote({ ...editNote, categorie: e.target.value })}>
                              {NOTE_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                            </select>
                            <textarea className="form-control form-control-sm mb-1" rows={3}
                              value={editNote.contenu} onChange={e => setEditNote({ ...editNote, contenu: e.target.value })} />
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-primary" onClick={handleSaveEditNote}><i className="bi bi-check2"></i></button>
                              <button className="btn btn-sm btn-light" onClick={() => setEditNote(null)}><i className="bi bi-x"></i></button>
                            </div>
                          </>
                        ) : (
                          <div className="d-flex align-items-start justify-content-between gap-2">
                            <div>
                              <span className="badge bg-primary bg-opacity-10 text-primary mb-1" style={{ fontSize: '0.7rem' }}>{note.categorie}</span>
                              <p className="mb-0" style={{ fontSize: '0.875rem' }}>{note.contenu}</p>
                              <small className="text-muted">
                                {new Date(note.created_at).toLocaleDateString('fr-FR')}
                                {note.enseignant_nom && ` · ${note.enseignant_nom}`}
                              </small>
                            </div>
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-light" onClick={() => setEditNote({ ...note })}>
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button className="btn btn-sm btn-light text-danger" onClick={() => handleDeleteNote(note.id)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
